<template>
  <div
    class="modal-body call-panel-body"
    style="flex: 1; overflow: hidden; display: flex; flex-direction: column"
  >
    <div class="chat-layout">
      <div v-if="isLoading" class="chat-loading-state">
        <LoadingBars size="sm" />
      </div>

      <template v-else>
        <div ref="messageListRef" class="chat-message-list">
          <div v-if="messages.length === 0" class="chat-empty-state">
            <div class="chat-empty-state__icon-wrap">
              <Icon icon="mdi:chat-processing-outline" class="chat-empty-state__icon" />
            </div>
            <p class="chat-empty-state__title">No messages yet</p>
            <p class="chat-empty-state__subtitle">
              Start an internal conversation about this contact — visible to everyone in your
              organization.
            </p>
          </div>

          <template v-for="item in messageGroups" :key="item.key">
            <div v-if="item.type === 'day'" class="chat-day-divider">
              <span>{{ item.label }}</span>
            </div>

            <div v-else class="chat-group" :class="{ 'chat-group--own': item.isOwn }">
              <div class="chat-group__avatar-col">
                <div
                  v-if="avatarUrl(item.messages[0]) && !failedAvatars.has(item.messages[0].id)"
                  class="chat-avatar chat-avatar--img"
                >
                  <img
                    :src="avatarUrl(item.messages[0])"
                    :alt="senderName(item.messages[0])"
                    @error="failedAvatars.add(item.messages[0].id)"
                  />
                </div>
                <div
                  v-else
                  class="chat-avatar"
                  :style="{ backgroundColor: avatarColor(item.messages[0]) }"
                >
                  {{ initials(item.messages[0]) }}
                </div>
              </div>

              <div class="chat-group__body">
                <div class="chat-group__meta">
                  <span class="chat-group__sender">{{ senderName(item.messages[0]) }}</span>
                  <span class="chat-group__time">{{
                    formatTimeOnly(item.messages[0].createdAt)
                  }}</span>
                </div>

                <div
                  v-for="(msg, msgIndex) in item.messages"
                  :key="msg.id"
                  class="chat-message-wrap"
                >
                  <div class="chat-bubble-row">
                    <div
                      class="chat-bubble"
                      :class="{ 'chat-bubble--mentioned': isMentioned(msg) }"
                      v-html="renderMessageHtml(msg)"
                    ></div>
                    <span v-if="msgIndex > 0" class="chat-bubble-row__time">{{
                      formatTimeOnly(msg.createdAt)
                    }}</span>
                    <button
                      type="button"
                      class="chat-bubble-row__react-btn"
                      title="React"
                      :ref="(el) => setReactTriggerRef(msg.id, el)"
                      @click="toggleReactionPicker(msg.id)"
                    >
                      <Icon icon="mdi:emoticon-happy-outline" width="15" height="15" />
                    </button>
                  </div>

                  <div
                    v-if="groupedReactions(msg).length > 0"
                    class="chat-message-reactions"
                    :class="{ 'chat-message-reactions--own-side': item.isOwn }"
                  >
                    <button
                      v-for="group in groupedReactions(msg)"
                      :key="group.value"
                      type="button"
                      class="chat-message-reaction"
                      :class="{ 'chat-message-reaction--own': group.own }"
                      :title="reactionTooltip(group)"
                      @click="group.own && handleRemoveReaction(msg.id)"
                    >
                      <span class="chat-message-reaction__emoji">{{ group.value }}</span>
                      <span v-if="group.count > 1" class="chat-message-reaction__count">{{
                        group.count
                      }}</span>
                    </button>
                  </div>

                  <MessageReactionPicker
                    :show="openReactionMessageId === msg.id"
                    :trigger-el="reactionTriggerRefs[msg.id] || null"
                    @select="(emoji) => handleReactionSelect(msg.id, emoji)"
                    @close="openReactionMessageId = null"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="chat-composer">
          <div class="chat-composer__box">
            <div class="chat-composer__input-shell">
              <div
                class="chat-composer__highlight"
                aria-hidden="true"
                v-html="draftHighlightHtml"
              ></div>
              <textarea
                ref="textareaRef"
                v-model="draft"
                class="chat-composer__textarea"
                rows="1"
                placeholder="Message your team... use @ to mention"
                @keydown.enter.exact.prevent="handleEnterKey"
                @input="handleComposerInput"
                @scroll="syncHighlightScroll"
                @keydown.esc="mentionQuery = null"
              />
            </div>
            <button
              type="button"
              class="chat-composer__send"
              :disabled="!draft.trim() || isSending"
              @click="handleSend"
            >
              <Icon icon="mdi:send" width="16" height="16" />
            </button>

            <div v-if="mentionCandidates.length > 0" class="mention-dropdown">
              <button
                v-for="user in mentionCandidates"
                :key="user.id"
                type="button"
                class="mention-dropdown__item"
                @mousedown.prevent="selectMention(user)"
              >
                <img
                  v-if="mentionAvatarUrl(user) && !failedMentionAvatars.has(user.id)"
                  class="mention-dropdown__avatar mention-dropdown__avatar--img"
                  :src="mentionAvatarUrl(user)"
                  :alt="user.fullName"
                  @error="failedMentionAvatars.add(user.id)"
                />
                <span
                  v-else
                  class="mention-dropdown__avatar"
                  :style="{ backgroundColor: colorFromName(user.fullName || '') }"
                  >{{ shortInitials(user.fullName) }}</span
                >
                <span class="mention-dropdown__name">{{ user.fullName }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import { LoadingBars } from "@/components/ui";
import MessageReactionPicker from "@/components/socials/MessageReactionPicker.vue";
import { useContactChatMessages } from "@/composables/contact-chat/useContactChatMessages";
import { useSendContactChatMessage } from "@/composables/contact-chat/useSendContactChatMessage";
import { useMarkContactChatRead } from "@/composables/contact-chat/useMarkContactChatRead";
import { useContactChatSocketListeners } from "@/composables/contact-chat/useContactChatSocketListeners";
import { useReactToContactChatMessage } from "@/composables/contact-chat/useReactToContactChatMessage";
import { useUsers } from "@/composables/organization/useUsers";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "@/composables/ui/useToast";
import { STORAGE_URL, USER_PROFILE_PIC_DIR } from "@/utils/const";

const MENTION_TOKEN_REGEX = /&lt;@([0-9a-fA-F-]{36})&gt;/g;
const MENTION_TRIGGER_REGEX = /(?:^|\s)@([\w.'-]*)$/;
const GROUP_GAP_MS = 5 * 60 * 1000;

const escapeHtml = (str) =>
  str.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]);
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const props = defineProps({
  selectedBucket: { type: Object, default: null },
  isActive: { type: Boolean, default: false },
});

const store = useStore();
const account = computed(() => store.getters.account?.data);
const queryClient = useQueryClient();
const $toast = useToast();

const contactId = computed(() => props.selectedBucket?.contact?.id || null);
const draft = ref("");
const isSending = ref(false);
const messageListRef = ref(null);
const textareaRef = ref(null);
const failedAvatars = ref(new Set());
const failedMentionAvatars = ref(new Set());

const { data, isLoading, refetch } = useContactChatMessages(contactId);
const messages = computed(() => data.value?.messages || []);

// Consecutive messages from the same sender within a short gap are folded
// into one visual group (avatar/name shown once), with day dividers between
// calendar days — same pattern most modern chat UIs use to cut down on
// repeated chrome for back-to-back messages.
const messageGroups = computed(() => {
  const groups = [];
  let lastDateKey = null;
  let currentGroup = null;

  for (const msg of messages.value) {
    const msgDate = new Date(msg.createdAt);
    const dateKey = msgDate.toDateString();

    if (dateKey !== lastDateKey) {
      groups.push({ type: "day", key: `day-${dateKey}`, label: formatDayLabel(msg.createdAt) });
      lastDateKey = dateKey;
      currentGroup = null;
    }

    const senderId = msg.createdById || "unknown";
    const lastMsg = currentGroup?.messages[currentGroup.messages.length - 1];
    const sameSenderRecently =
      currentGroup &&
      currentGroup.senderId === senderId &&
      msgDate - new Date(lastMsg.createdAt) < GROUP_GAP_MS;

    if (sameSenderRecently) {
      currentGroup.messages.push(msg);
    } else {
      currentGroup = {
        type: "group",
        key: `group-${msg.id}`,
        senderId,
        isOwn: isOwnMessage(msg),
        messages: [msg],
      };
      groups.push(currentGroup);
    }
  }

  return groups;
});

// Org members list — reused for both the @mention autocomplete and resolving
// mentioned userIds back to display names when rendering received messages.
const usersParams = computed(() =>
  account.value?.organizationId
    ? { organizationId: account.value.organizationId, limit: 200 }
    : null
);
const { data: usersData } = useUsers(usersParams);
const orgUsers = computed(() => usersData.value || []);
const usersById = computed(() => {
  const map = new Map();
  for (const user of orgUsers.value) map.set(user.id, user);
  return map;
});

// Tracks the userId behind each "@FullName" string inserted via the
// autocomplete, in selection order, so handleSend can convert visible names
// back into <@userId> tokens without needing a rich-text/contenteditable
// composer. Kept as an ordered list (not a Map keyed by name) so two
// different users sharing the same full name — or one name that's a prefix
// of another (e.g. "Jon" and "Jon Anderson") — each resolve to the correct
// userId instead of colliding.
const pendingMentions = ref([]);
const mentionQuery = ref(null);
const mentionCandidates = computed(() => {
  if (mentionQuery.value === null) return [];
  const q = mentionQuery.value.toLowerCase();
  return orgUsers.value
    .filter((user) => user.id !== account.value?.id)
    .filter((user) => (user.fullName || "").toLowerCase().includes(q))
    .slice(0, 6);
});

const { mutateAsync: sendMessage } = useSendContactChatMessage();
const { mutateAsync: markRead } = useMarkContactChatRead();
const { addReaction, removeReaction } = useReactToContactChatMessage();

const openReactionMessageId = ref(null);
const reactionTriggerRefs = ref({});

const setReactTriggerRef = (messageId, el) => {
  reactionTriggerRefs.value[messageId] = el;
};

const toggleReactionPicker = (messageId) => {
  openReactionMessageId.value = openReactionMessageId.value === messageId ? null : messageId;
};

// Reactions come back from the API as one flat { value, userId } entry per
// user (a user can only have one active reaction). Grouped here by emoji so
// the UI shows a single pill per distinct emoji with a count, Slack-style,
// instead of a pill per person. Computed once per messages/users change and
// looked up by id, rather than regrouped on every template access.
const groupedReactionsByMessageId = computed(() => {
  const result = new Map();
  for (const msg of messages.value) {
    const reactions = msg.reactions || [];
    if (reactions.length === 0) continue;

    const byValue = new Map();
    for (const r of reactions) {
      const existing = byValue.get(r.value);
      if (existing) {
        existing.userIds.push(r.userId);
      } else {
        byValue.set(r.value, { value: r.value, userIds: [r.userId] });
      }
    }

    result.set(
      msg.id,
      Array.from(byValue.values()).map((group) => ({
        value: group.value,
        count: group.userIds.length,
        own: !!account.value?.id && group.userIds.includes(account.value.id),
        names: group.userIds.map((userId) =>
          userId === account.value?.id ? "You" : usersById.value.get(userId)?.fullName || "Someone"
        ),
      }))
    );
  }
  return result;
});

const groupedReactions = (msg) => groupedReactionsByMessageId.value.get(msg.id) || [];

const reactionTooltip = (group) => {
  const names = group.names.join(", ");
  return group.own ? `${names} · Click to remove your reaction` : names;
};

const handleReactionSelect = async (messageId, emoji) => {
  openReactionMessageId.value = null;
  if (!contactId.value) return;
  try {
    await addReaction({ contactId: contactId.value, messageId, reaction: emoji });
  } catch (error) {
    $toast?.error("Failed to add reaction. Please try again.");
  }
};

const handleRemoveReaction = async (messageId) => {
  if (!contactId.value) return;
  try {
    await removeReaction({ contactId: contactId.value, messageId });
  } catch (error) {
    $toast?.error("Failed to remove reaction. Please try again.");
  }
};

const scrollToBottom = () => {
  if (messageListRef.value) {
    messageListRef.value.scrollTop = messageListRef.value.scrollHeight;
  }
};

function isOwnMessage(msg) {
  return msg.createdById === account.value?.id;
}

const senderName = (msg) => {
  if (isOwnMessage(msg)) return "You";
  return msg.createdBy?.fullName || "Teammate";
};

const profilePicOf = (msg) =>
  isOwnMessage(msg) ? account.value?.profilePic : msg.createdBy?.profilePic;

const avatarUrl = (msg) => {
  const profilePic = profilePicOf(msg);
  return profilePic ? `${STORAGE_URL}/${USER_PROFILE_PIC_DIR}/${profilePic}` : null;
};

const initials = (msg) => {
  const name = senderName(msg);
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const colorFromName = (name) => {
  const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const avatarColor = (msg) => (isOwnMessage(msg) ? "#3e3efe" : colorFromName(senderName(msg)));

const isMentioned = (msg) =>
  !!account.value?.id && (msg.mentionedUserIds || []).includes(account.value.id);

const shortInitials = (name) => {
  const parts = (name || "").trim().split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name || "").substring(0, 2).toUpperCase();
};

const mentionAvatarUrl = (user) =>
  user?.profilePic ? `${STORAGE_URL}/${USER_PROFILE_PIC_DIR}/${user.profilePic}` : null;

// Replaces <@userId> tokens (escaped as &lt;@userId&gt; after escapeHtml) with
// a styled "@FullName" span, resolving names from the org members list. This
// is the only visual treatment mentions get — the surrounding bubble/message
// styling is left untouched.
const renderMessageHtml = (msg) => {
  const escaped = escapeHtml(msg.message || "");
  return escaped.replace(MENTION_TOKEN_REGEX, (_, userId) => {
    const user = usersById.value.get(userId);
    const name = escapeHtml(user?.fullName || "Someone");
    const isSelf = userId === account.value?.id;
    return `<span class="chat-mention${isSelf ? " chat-mention--self" : ""}">@${name}</span>`;
  });
};

const handleComposerInput = () => {
  const el = textareaRef.value;
  if (!el) return;
  const caret = el.selectionStart ?? draft.value.length;
  const match = draft.value.slice(0, caret).match(MENTION_TRIGGER_REGEX);
  mentionQuery.value = match ? match[1] : null;
};

// Mirrors the composer text behind the real textarea so tracked mentions can
// show a highlighted background while typing — a plain <textarea> can't style
// part of its own text, so this invisible-text layer sits underneath and only
// its mention <span> backgrounds are visible through the transparent textarea.
const draftHighlightHtml = computed(() => {
  const text = draft.value;
  if (pendingMentions.value.length === 0 || !text) return escapeHtml(text);

  const names = Array.from(new Set(pendingMentions.value.map((m) => m.name)))
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  const regex = new RegExp(`@(?:${names.join("|")})\\b`, "g");

  let result = "";
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    result += escapeHtml(text.slice(lastIndex, match.index));
    result += `<span class="composer-mention-highlight">${escapeHtml(match[0])}</span>`;
    lastIndex = match.index + match[0].length;
  }
  result += escapeHtml(text.slice(lastIndex));
  return result;
});

const syncHighlightScroll = (event) => {
  const highlightEl = event.target.previousElementSibling;
  if (highlightEl) highlightEl.scrollTop = event.target.scrollTop;
};

const selectMention = (user) => {
  const el = textareaRef.value;
  const caret = el?.selectionStart ?? draft.value.length;
  const before = draft.value.slice(0, caret).replace(MENTION_TRIGGER_REGEX, `@${user.fullName} `);
  const after = draft.value.slice(caret);
  draft.value = before + after;
  pendingMentions.value.push({ name: user.fullName, userId: user.id });
  mentionQuery.value = null;

  nextTick(() => {
    if (!el) return;
    el.focus();
    el.setSelectionRange(before.length, before.length);
  });
};

// Converts any "@FullName" text that came from a tracked autocomplete
// selection into the <@userId> wire token the backend expects. Names typed
// manually without picking from the dropdown are left as plain text.
//
// Matches are consumed left-to-right in selection order (longest name first
// at each position) rather than blanket-replacing by name, so a name that's
// a prefix of another selected name (e.g. "Jon" vs. "Jon Anderson") can't
// steal the wrong match, and two different users who share a full name each
// resolve to the userId they were actually picked for.
const tokenizeMentions = (text) => {
  if (pendingMentions.value.length === 0) return text;

  const remaining = pendingMentions.value.map((m) => ({ ...m }));
  const names = Array.from(new Set(remaining.map((m) => m.name)))
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  const regex = new RegExp(`@(?:${names.join("|")})\\b`, "g");

  return text.replace(regex, (match) => {
    const name = match.slice(1);
    const idx = remaining.findIndex((m) => m.name === name);
    if (idx === -1) return match;
    const [{ userId }] = remaining.splice(idx, 1);
    return `<@${userId}>`;
  });
};

function formatDayLabel(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

const formatTimeOnly = (timestamp) =>
  new Date(timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const handleEnterKey = () => {
  if (mentionCandidates.value.length > 0) {
    selectMention(mentionCandidates.value[0]);
    return;
  }
  handleSend();
};

const handleSend = async () => {
  const raw = draft.value.trim();
  if (!raw || !contactId.value || isSending.value) return;

  const message = tokenizeMentions(raw);
  const mentionsSnapshot = pendingMentions.value;

  isSending.value = true;
  draft.value = "";
  pendingMentions.value = [];
  mentionQuery.value = null;
  try {
    await sendMessage({ contactId: contactId.value, message });
    await nextTick();
    scrollToBottom();
  } catch (error) {
    draft.value = raw;
    pendingMentions.value = mentionsSnapshot;
    $toast?.error("Failed to send message. Please try again.");
  } finally {
    isSending.value = false;
  }
};

useContactChatSocketListeners({
  onChatMessage: (data) => {
    if (data?.contactId !== contactId.value) return;
    queryClient.invalidateQueries({ queryKey: ["contact-chat-messages", contactId.value] });
    // Also invalidate the badge count so the Chat tab dot updates live for a
    // teammate who has the modal open but isn't on this tab right now.
    queryClient.invalidateQueries({ queryKey: ["interactionCounts", contactId.value] });
    if (props.isActive) {
      nextTick(() => scrollToBottom());
      // The tab is already open and visible, so the incoming message is
      // immediately seen — mark it read now instead of leaving the badge to
      // pop back on until the user switches tabs away and back.
      markRead(contactId.value);
    }
  },
  onReaction: (data) => {
    if (data?.contactId !== contactId.value) return;
    queryClient.invalidateQueries({ queryKey: ["contact-chat-messages", contactId.value] });
  },
});

watch(
  () => props.isActive,
  async (isActive) => {
    if (isActive && contactId.value) {
      await refetch();
      await nextTick();
      scrollToBottom();
      markRead(contactId.value);
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.chat-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.chat-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.chat-message-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  background: #fbfbfd;
}

.chat-message-list::-webkit-scrollbar {
  width: 6px;
}

.chat-message-list::-webkit-scrollbar-track {
  background: transparent;
}

.chat-message-list::-webkit-scrollbar-thumb {
  background: #e2e2ea;
  border-radius: 3px;
}

.chat-empty-state {
  margin: auto;
  text-align: center;
  color: #6b7280;
  max-width: 280px;
}

.chat-empty-state__icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #eef0ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
}

.chat-empty-state__icon {
  font-size: 1.5rem;
  color: #3e3efe;
}

.chat-empty-state__title {
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
  font-size: 0.9375rem;
}

.chat-empty-state__subtitle {
  font-size: 0.8125rem;
  line-height: 1.5;
}

.chat-day-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0.25rem 0 0.6rem;
}

.chat-day-divider::before,
.chat-day-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #ececf1;
}

.chat-day-divider span {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.chat-group {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.chat-group--own {
  flex-direction: row-reverse;
}

.chat-group__avatar-col {
  flex-shrink: 0;
  padding-top: 2px;
}

.chat-avatar {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  box-shadow:
    0 0 0 2px #fbfbfd,
    0 1px 3px rgba(0, 0, 0, 0.08);
}

.chat-avatar--img {
  overflow: hidden;
}

.chat-avatar--img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-group__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 78%;
}

.chat-group--own .chat-group__body {
  align-items: flex-end;
}

.chat-group__meta {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  margin-bottom: 2px;
  padding: 0 2px;
}

.chat-group__sender {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #111827;
}

.chat-group__time {
  font-size: 0.6875rem;
  color: #9ca3af;
}

.chat-bubble-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.chat-group--own .chat-bubble-row {
  flex-direction: row-reverse;
}

.chat-bubble-row__time {
  font-size: 0.6875rem;
  color: #9ca3af;
  opacity: 0;
  transition: opacity 0.15s ease;
  white-space: nowrap;
  padding-bottom: 4px;
}

.chat-bubble-row:hover .chat-bubble-row__time {
  opacity: 1;
}

.chat-bubble-row__react-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  opacity: 0;
  flex-shrink: 0;
  transition:
    opacity 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;
}

.chat-bubble-row:hover .chat-bubble-row__react-btn {
  opacity: 1;
}

.chat-bubble-row__react-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.chat-message-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 3px 0 2px;
  padding: 0 2px;
}

.chat-message-reactions--own-side {
  justify-content: flex-end;
}

.chat-message-reaction {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border: 1px solid #ececf1;
  border-radius: 999px;
  background: #fff;
  cursor: default;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;
}

.chat-message-reaction--own {
  cursor: pointer;
  background: #eaf1ff;
  border-color: #cfe0ff;
}

.chat-message-reaction--own:hover {
  background: #d9ecff;
  transform: scale(1.05);
}

.chat-message-reaction__emoji {
  font-size: 0.875rem;
  line-height: 1;
}

.chat-message-reaction__count {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.6875rem;
}

.chat-bubble {
  padding: 0.45rem 0.75rem;
  border-radius: 16px;
  font-size: 0.875rem;
  line-height: 1.4;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.chat-group:not(.chat-group--own) .chat-bubble {
  background: #fff;
  color: #18181b;
  border: 1px solid #ececf1;
  border-bottom-left-radius: 5px;
}

.chat-group--own .chat-bubble {
  background: linear-gradient(135deg, #2d3bff 0%, #4f63ff 100%);
  color: #fff;
  border-bottom-right-radius: 5px;
}

/* Whole-message highlight for anything that mentions the current user —
   this, not the inline chip, is what makes a mention jump out while
   scanning the thread (same idea as Slack's highlighted-row treatment). */
.chat-bubble--mentioned {
  background: #fffaf0;
  border-color: #fbd786;
}

/* Mention chip: a crisp, high-contrast pill rather than a same-color tint,
   so it pops the same amount whether it sits on the white bubble or the
   blue own-message bubble. Uses :deep() because the mention <span> is
   injected via v-html (renderMessageHtml), so it never receives Vue's
   scoped data-v attribute and a plain (non-deep) scoped selector would
   never match it. */
:deep(.chat-mention) {
  display: inline-block;
  font-weight: 700;
  line-height: 1.35;
  color: #1d4ed8;
  background: #eaf1ff;
  border-radius: 5px;
  padding: 0px 5px;
  margin: 0 1px;
}

/* On the dark own-message bubble a same tint would vanish, so it becomes a
   near-solid white pill with the same blue text — same contrast ratio,
   inverted for the darker surface. */
.chat-group--own :deep(.chat-mention) {
  color: #1d4ed8;
  background: #fff;
}

/* When you're the one mentioned, the chip switches to amber to match the
   whole-bubble highlight above it, so the two cues reinforce each other. */
:deep(.chat-mention--self) {
  color: #92400e;
  background: #fde3a7;
}

.chat-group--own :deep(.chat-mention--self) {
  color: #92400e;
  background: #ffe9a8;
}

.chat-composer {
  padding: 0.6rem 0.85rem 0.75rem;
  border-top: 1px solid #ececf1;
  background: #fff;
}

.chat-composer__box {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #f5f5f8;
  border: 1px solid transparent;
  border-radius: 20px;
  padding: 6px 6px 6px 14px;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.chat-composer__box:focus-within {
  background: #fff;
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.chat-composer__input-shell {
  position: relative;
  flex: 1;
}

.chat-composer__highlight,
.chat-composer__textarea {
  padding: 6px 0;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
  max-height: 90px;
}

.chat-composer__highlight {
  position: absolute;
  inset: 0;
  color: transparent;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: hidden;
  pointer-events: none;
}

.chat-composer__textarea {
  position: relative;
  width: 100%;
  resize: none;
  border: none;
  background: transparent;
  outline: none;
}

.chat-composer__send {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #2d3bff 0%, #4f63ff 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.12s ease,
    opacity 0.12s ease;
}

.chat-composer__send:hover:not(:disabled) {
  transform: scale(1.06);
}

:deep(.composer-mention-highlight) {
  background: #d9ecff;
  border-radius: 3px;
}

.chat-composer__send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mention-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  width: 220px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 20;
  padding: 4px;
}

.mention-dropdown__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}

.mention-dropdown__item:hover {
  background: #f3f4f6;
}

.mention-dropdown__avatar {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  background: #6366f1;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
}

.mention-dropdown__avatar--img {
  object-fit: cover;
}

.mention-dropdown__name {
  font-size: 0.8125rem;
  color: #18181b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
